import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { RootState } from '../../store';
import { useBreadcrumb } from '../../layouts/BreadcrumbContext';
import {
  useGetCounsellingStatusQuery,
  useGetOneCnsnSubSumryQuery,
} from './counselling.services';
import { exportToExcel, ExcelColumn } from '../../shared/export/excel';

/**
 * Migrated from Angular `CounsellingStatusComponent`
 * (src/app/views/pages/counselling/counselling-status).
 *
 * Route: /counselling/status
 */

// ── Column definitions ───────────────────────────────────────────────────────

interface Col {
  field: string;
  header: string;
}

const MAIN_COLS: Col[] = [
  { field: 'Directorate', header: 'Directorate' },
  { field: 'Limits', header: 'Location' },
  { field: 'TransType', header: 'TransType' },
  { field: 'Candidates', header: 'Candidates' },
  { field: 'AbsentCount', header: 'Absent' },
  { field: 'NotWillingCount', header: 'Relinquished/Not Willing' },
  { field: 'OrderCount', header: 'Completed' },
  { field: 'Status', header: 'Status' },
];

const SUB_COLS: Col[] = [
  { field: 'title', header: 'Counselling' },
  { field: 'subjects', header: 'Subject' },
  { field: 'Total', header: 'Total Candidates' },
  { field: 'AbsentCount', header: 'Absent' },
  { field: 'NotWillingCount', header: 'Relinquished/Not Willing' },
  { field: 'OrderCount', header: 'Completed' },
  { field: 'Pending', header: 'Pending' },
];

// ── Role-based filter (same as Angular) ──────────────────────────────────────

function filterByRole(
  result: any[],
  userTypeId: string,
  userTypeId1: string
): any[] {
  if (userTypeId === '5' && userTypeId1 === '1') {
    return result.filter((ft) => ft.DirectorateId === '1'); // DEE
  } else if (userTypeId === '5' && userTypeId1 === '2') {
    return result.filter((ft) => ft.DirectorateId === '2'); // DSE
  } else if (
    (userTypeId === '5' && userTypeId1 === '3') ||
    (userTypeId === '9' && userTypeId1 !== '29')
  ) {
    // STATE, CEO
    return result;
  } else if (
    (userTypeId === '5' && userTypeId1 === '29') ||
    (userTypeId === '9' && userTypeId1 === '29') ||
    (userTypeId === '6' && userTypeId1 === '29')
  ) {
    return result.filter((ft) => ft.DirectorateId === '4'); // GCC
  } else if (userTypeId === '5' && userTypeId1 === '6') {
    return result.filter((ft) => ft.DirectorateId === '5'); // SCERT
  } else if (userTypeId === '5' && userTypeId1 === '8') {
    return result.filter((ft) => ft.DirectorateId === '6'); // Kallar schools
  } else {
    return result; // ELSE
  }
}

// ── Component ────────────────────────────────────────────────────────────────

const CounsellingStatus = () => {
  const { usertypeid, emisUsertype1, loaded } = useSelector(
    (state: RootState) => state.session
  );
  const { setSubItems, clearSubItems } = useBreadcrumb();

  // View state: main table or subject summary
  const [selectedCnsId, setSelectedCnsId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const isSubView = !!selectedCnsId;

  // Column chooser
  const [selectedMainCols, setSelectedMainCols] = useState<Col[]>(MAIN_COLS);
  const [selectedSubCols, setSelectedSubCols] = useState<Col[]>(SUB_COLS);

  // Global filter
  const [globalFilterMain, setGlobalFilterMain] = useState('');
  const [globalFilterSub, setGlobalFilterSub] = useState('');

  const dtMain = useRef<DataTable<any[]>>(null);
  const dtSub = useRef<DataTable<any[]>>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: statusRaw, isFetching: statusLoading } =
    useGetCounsellingStatusQuery(undefined, { skip: !loaded });

  const { data: subSummaryRaw, isFetching: subLoading } =
    useGetOneCnsnSubSumryQuery(
      { counsellingId: selectedCnsId },
      { skip: !selectedCnsId }
    );

  // ── Derived data ─────────────────────────────────────────────────────────

  const mainTableData = useMemo(() => {
    if (!statusRaw?.dataStatus) return [];
    return filterByRole(statusRaw.result, usertypeid, emisUsertype1);
  }, [statusRaw, usertypeid, emisUsertype1]);

  const subTableData = useMemo(() => {
    if (!subSummaryRaw?.dataStatus) return [];
    return subSummaryRaw.result;
  }, [subSummaryRaw]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleRowSelect = useCallback(
    (e: any) => {
      const row = e.data;
      setSelectedCnsId(row.CnsId);
      setSelectedTitle(row.Title);
      setSubItems([
        {
          label: row.Title,
          onClick: () => {
            setSelectedCnsId(null);
            setSelectedTitle('');
            clearSubItems();
          },
        },
      ]);
    },
    [setSubItems, clearSubItems]
  );

  // Clear breadcrumb sub-items on unmount
  useEffect(() => {
    return () => clearSubItems();
  }, [clearSubItems]);

  const handleExportExcel = useCallback(() => {
    if (isSubView) {
      if (!subTableData.length) return;
      const excelData = subTableData.map((row: any) => ({
        Title: row.title || '-',
        Subject: row.subjects || '-',
        'Total Candidates': Number(row.Total) || 0,
        Absent: Number(row.AbsentCount) || 0,
        'Relinquished/Not Willing': Number(row.NotWillingCount) || 0,
        Completed: Number(row.OrderCount) || 0,
        Pending: Number(row.Pending) || 0,
      }));
      const columns: ExcelColumn[] = Object.keys(excelData[0]).map((key) => ({
        key,
        label: key,
      }));
      exportToExcel({
        data: excelData,
        columns,
        fileName: `Counselling Status_export_${Date.now()}`,
        sheetName: 'Counselling Status',
      });
    } else {
      if (!mainTableData.length) return;
      const excelData = mainTableData.map((row: any) => ({
        FrmDte: row.FrmDte || '-',
        ToDte: row.ToDte || '-',
        Title: row.Title || '-',
        Directorate: row.Directorate || '-',
        Limits: row.Limits || '-',
        TransType: row.TransType || '-',
        Status: row.Status || '-',
        Candidates: row.Candidates || '-',
        OrderCount: row.OrderCount || '-',
        AbsentCount: row.AbsentCount || '-',
        NotWillingCount: row.NotWillingCount || '-',
      }));
      const columns: ExcelColumn[] = Object.keys(excelData[0]).map((key) => ({
        key,
        label: key,
      }));
      exportToExcel({
        data: excelData,
        columns,
        fileName: `Counselling Status_export_${Date.now()}`,
        sheetName: 'Counselling Status',
      });
    }
  }, [isSubView, mainTableData, subTableData]);

  // ── Shared toolbar ───────────────────────────────────────────────────────

  const renderToolbar = (
    allCols: Col[],
    selectedCols: Col[],
    setSelectedCols: (val: Col[]) => void,
    globalFilter: string,
    setGlobalFilter: (val: string) => void,
    dataLength: number
  ) => (
    <div className="row">
      <div className="col-md-3 col-12 mb-2">
        <MultiSelect
          value={selectedCols}
          options={allCols}
          optionLabel="header"
          filter
          filterBy="header"
          onChange={(e) => {
            const selected = allCols.filter((col) => e.value.includes(col));
            setSelectedCols(selected);
          }}
          selectedItemsLabel="{0} columns selected"
          placeholder="Choose Columns"
          className="w-100"
        />
      </div>
      <div className="col-md-7 col-12 text-end">
        <span className="p-input-icon-left">
          <InputText
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search keyword"
          />
        </span>
      </div>
      <div className="col-md-2 col-12 text-end">
        <Button
          type="button"
          label="Export As Excel"
          icon="pi pi-file-excel"
          className="p-button-success p-button-sm rounded"
          onClick={handleExportExcel}
          disabled={dataLength === 0}
        />
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Main Table */}
      {!isSubView && (
        <div className="card p-4">
          <DataTable
            ref={dtMain}
            value={mainTableData}
            paginator
            rows={10}
            stripedRows
            rowsPerPageOptions={[10, 25, 50]}
            loading={statusLoading}
            showGridlines
            resizableColumns
            rowHover
            dataKey="CnsId"
            selectionMode="single"
            onRowSelect={handleRowSelect}
            globalFilter={globalFilterMain}
            globalFilterFields={selectedMainCols
              .map((c) => c.field)
              .concat(['Title', 'FrmDte'])}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            emptyMessage="No Records Found"
            tableStyle={{ minWidth: '70rem' }}
            header={renderToolbar(
              MAIN_COLS,
              selectedMainCols,
              setSelectedMainCols,
              globalFilterMain,
              setGlobalFilterMain,
              mainTableData.length
            )}
          >
            <Column
              header="#"
              body={(_data, options) => options.rowIndex + 1}
              style={{ width: '3rem' }}
            />
            <Column
              field="Title"
              header="Counselling"
              sortable
              filterPlaceholder="Search"
              body={(row) => (
                <span className="text-primary link-hover">{row.Title}</span>
              )}
              style={{ minWidth: '12rem' }}
            />
            <Column
              field="FrmDte"
              header="Date"
              sortable
              filterPlaceholder="Search"
              style={{ minWidth: '8rem' }}
            />
            {selectedMainCols.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                filterPlaceholder="Search"
                body={(row) => row[col.field] || '-'}
                style={{ minWidth: '8rem' }}
              />
            ))}
          </DataTable>
        </div>
      )}

      {/* Subject Summary Table */}
      {isSubView && (
        <div className="card p-4">
          <DataTable
            ref={dtSub}
            value={subTableData}
            paginator
            stripedRows
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={subLoading}
            showGridlines
            resizableColumns
            rowHover
            dataKey="CnsId"
            globalFilter={globalFilterSub}
            globalFilterFields={selectedSubCols.map((c) => c.field)}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            emptyMessage="No Records Found"
            tableStyle={{ minWidth: '60rem' }}
            header={renderToolbar(
              SUB_COLS,
              selectedSubCols,
              setSelectedSubCols,
              globalFilterSub,
              setGlobalFilterSub,
              subTableData.length
            )}
          >
            <Column
              header="#"
              body={(_data, options) => options.rowIndex + 1}
              style={{ width: '3rem' }}
            />
            {selectedSubCols.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                filterPlaceholder="Search"
                body={(row) => row[col.field] || '-'}
                style={{ minWidth: '8rem' }}
              />
            ))}
          </DataTable>
        </div>
      )}
    </div>
  );
};

export default CounsellingStatus;
